import type { AwsCredentialIdentity } from '@smithy/types';
import isString from 'lodash/isString';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { LocationDto, LocationType } from './bucketConfigDto';
import type { AwsAccessType} from './ingestStorageConfigDto';

// This value is meant to be used as the upload location ID when
// we want to upload files to our bucket
export const MIMIR_INCOMING_MEDIA_BUCKET = 'MIMIR_INCOMING_MEDIA_BUCKET';

// This value is meant to be used as the location ID when
// we want to upload files to the default upload location,
// that could be either a location defined by the customer or our incoming
// media bucket if the customer hasn't configured a default upload location
export const DEFAULT_UPLOAD_LOCATION = 'DEFAULT_UPLOAD_LOCATION';

export const DEFAULT_ARCHIVE_BUCKET = 'DEFAULT_ARCHIVE_BUCKET';

export type UploadLocationId = 'DEFAULT_UPLOAD_LOCATION' | 'MIMIR_INCOMING_MEDIA_BUCKET' | 'DEFAULT_ARCHIVE_BUCKET' | string;

export interface UploadLocation extends LocationDto {
  region?: string;

  /**
   * TODO: This property might be obsolete, since even though the
   * makeDefaultUploadConfig() function sets it to true, the actual presigned
   * PUT URLs that our server backend gives to our client does not use
   * accelerated URLs.
   */
  enableS3Acceleration?: boolean;

  /**
   * When true, no triggers will be set on the bucket (only for upload locations).
   */
  skipProcessing?: boolean;

  bucketEndpoint?: string;
  pathPrefix?: string;

  // Whether to delete from the bucket when Mimir item is permanently deleted.
  deleteEnabled?: boolean;
  /**
   * stores array of groups id which has the permission to see the location
   * VisibleTo property already existed in IngestStorage, so to keep consistency using the name visibleToGroupList everywhere instead of visibleTo
   */
  visibleToGroupList?: string[] | null;

  // TODO: IAM-ROLE_ACCESS propagate following two properties where needed
  accessType?: AwsAccessType;
  externalId?: string;
}

export interface UpdateDefaultUploadLocationDto {
  id: string;
}

export type CreateUploadLocationDto = UploadLocation & {
  accessKey: string;
  secretKey: string;

  ingestType: 'default_upload_location';
};

export type UploadBucketConfigDto = UploadLocation & {
  isExternalBucket: boolean;

  /**
   * The target key to use when isExternalBucket is false.
   *
   * Most call paths replace or overwrites the targetKey when it sees that
   * isExternalBucket is true.
   */
  targetKey: string;
  credentials: AwsCredentialIdentity | undefined;
  bucketEndpoint?: string;
};

export type UploadLocationClientDto = UploadLocation & {
  areCredentialsSaved: boolean;
  id: string;

  /**
   * Kelda id is included if we're dealing with an upload location that is actually a Kelda. The configuration itself
   * has a different ID. This is to be used on the client to be able to tell that the current default upload location
   * refers to a specific kelda, to prevent the user from for example deleting it.
   */
  keldaId?: string;
};

export type GetItemUploadLockResponseDto = {
  isUploadLocked: boolean;
};

export interface CreateItemUploadLockResponseDto {
  uploadLocationId: UploadLocationId;
  targetKey: string;
  type: LocationType;
  bucket: string;

  keldaId?: string;
  isExternalBucket?: boolean;
  uploadSignedUrl?: string;
  storageConfigId?: string;
}

export interface CreateItemMultipartUploadResponseDto extends CreateItemUploadLockResponseDto {
  uploadId: string;
  uploadPartSize: number;
  uploadPartCount: number;
}

export function isMultipartUploadResponseDto(dto: CreateItemUploadLockResponseDto): dto is CreateItemMultipartUploadResponseDto {
  const maybe = dto as Partial<CreateItemMultipartUploadResponseDto>;
  return 'uploadId' in maybe && 'uploadPartSize' in maybe && 'uploadPartCount' in maybe;
}

export type S3CloneFailure = {
  bucketEndpoint: string
};

export type CreateItemUploadLockFailureDto = {
  s3CloneFailure: S3CloneFailure | null;
};

export type CreateItemUploadLockRequestDto = {
  lockOwnerInstanceId: string;

  // The possible values are:
  // - MIMIR_INCOMING_MEDIA_BUCKET
  // - DEFAULT_UPLOAD_LOCATION
  // - A valid reference to an upload ingest configuration
  uploadLocationId?: UploadLocationId;

  /**
   * If provided, we will use this value instead of the item's originalFileName when deriving the upload target filename.
   * For placeholders that do not have originalFileName, this param is required.
   */
  fileName?: string;

  isMultiPartUpload?: boolean;
  chunkSize?: number;
  fileSize?: number;
  contentType?: string;
  targetFolder?: string;
};
export type CreateItemUploadLockDto = UploadBucketConfigDto & {
  storageConfigId?: string;
  uploadLocationId: UploadLocationId;
  keldaId?: string
};

export type ItemUploadSignResponseDto = {
  signature: string;
};

export type CreateItemUploadPartsSignedUrlsRequestDto = {
  uploadLocationId: UploadLocationId;
  targetKey: string;
  uploadId: string;
  firstPartNumber: number;
  partCount: number;
};

export type CreateItemUploadPartsSignedUrlsResponseDto = {
  uploadPartsSignedUrls?: string[];
};
export type UploadPartDto = {
  PartNumber: number;
  ETag: string;
};

export const isInstanceOfUpdateUploadLocationDto = (obj: unknown): obj is UpdateDefaultUploadLocationDto => {
  if (!isObject(obj)) return false;
  return 'id' in obj;
};

export const isInstanceOfCreateUploadLocationDto = (obj: unknown): obj is CreateUploadLocationDto => {
  if (!isObject(obj)) return false;

  const possibleDto = obj as Partial<CreateUploadLocationDto>;
  return isString(possibleDto.accessKey)
    && (isString(possibleDto.secretKey) || (possibleDto.accessType === 'assume-role' && isString(possibleDto.externalId)))
    && possibleDto.ingestType === 'default_upload_location'
    && (isString(possibleDto.bucketEndpoint) || possibleDto.type === 's3Bucket');
};
